<template>
  <div class="app-shell" :style="themeStyle" :dir="isRTL ? 'rtl' : 'ltr'">
    <header class="hero">
      <img :src="brandConfig.bannerImage" :alt="brandConfig.serviceName" class="hero__banner" />
      <div class="hero__shade" aria-hidden="true"></div>
      <img v-if="brandConfig.showLogoOverlay !== false" :src="brandConfig.logoImage" :alt="brandConfig.serviceName" class="hero__logo" />
      <LanguageToggle class="hero__lang" />
    </header>

    <main class="app-main">
      <router-view />
    </main>

    <footer class="app-footer">
      <p class="app-footer__note">{{ t('footerNote') }}</p>
      <a
        v-if="brandConfig.termsConditionsUrl"
        class="text-link"
        :href="brandConfig.termsConditionsUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ t('termsConditions') }}
      </a>
    </footer>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useBrandStore } from '@/stores/brandStore'
import userAuthenticate from '@/functions/userAuthenticate'
import LanguageToggle from '@/components/LanguageToggle.vue'

const brandStore = useBrandStore()
const { brandConfig, themeStyle, isRTL } = storeToRefs(brandStore)
const { t } = brandStore

onMounted(userAuthenticate)
</script>

<style scoped>
.app-shell {
  max-width: var(--app-max-width);
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--color-bg);
  color: var(--color-text);
  font-family: var(--font-family);
}

.hero {
  position: relative;
  overflow: hidden;
  background: var(--color-surface);
}

/* Design brief: 700x555 banner, shown whole (no crop) so the wordmark at its foot stays intact. */
.hero__banner {
  width: 100%;
  height: auto;
  display: block;
}

/* Only a short feather into the page ground at the foot; the artwork's own title sits just above it. */
.hero__shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(11, 14, 23, 0.25) 0%, rgba(11, 14, 23, 0) 20%, rgba(11, 14, 23, 0) 90%, var(--color-bg) 100%);
  pointer-events: none;
}

/* Design brief: 160x55 logo. */
.hero__logo {
  position: absolute;
  top: var(--spacing-md);
  left: var(--spacing-md);
  width: 160px;
  height: auto;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5));
}

.hero__lang {
  position: absolute;
  top: var(--spacing-md);
  right: var(--spacing-md);
}

.app-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 var(--spacing-lg);
}

.app-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-2xl) var(--spacing-lg) var(--spacing-xl);
  text-align: center;
}

.app-footer__note {
  font-size: 0.78rem;
  line-height: 1.6;
  color: var(--color-text-secondary);
  max-width: 40ch;
}
</style>
